import type { KeyboardEvent } from 'react';

/**
 * Rows the arrows may land on: every `MenuListItem` (they all carry the
 * `flashka-menu-row` marker) that isn't disabled — `<button>` rows express
 * that as `disabled`, `as="a"` / `as="div"` rows as `aria-disabled`.
 */
const ROW_SELECTOR = '.flashka-menu-row:not([disabled]):not([aria-disabled="true"])';

/**
 * Roving Up/Down navigation for a menu panel — **web**.
 *
 * `DropdownMenu` wires this for every menu it renders, so `Dropdown`,
 * `Select`, `DropdownSegmentedButton` and submenu flyouts all get it: the
 * arrows walk focus through the rows, wrapping at both ends, and scroll the
 * focused row into view when the list is taller than the panel.
 *
 * Focus is the whole mechanism — the rows are real `<button>`s, so Enter /
 * Space activate them for free and `MenuHighlight` follows the focus event.
 * It also works from a `Select`'s search field: the field keeps the caret,
 * the list keeps the arrows.
 */
export function menuArrowKeyNav(event: KeyboardEvent<HTMLElement>): void {
  const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
  if (step === 0 || event.altKey || event.metaKey || event.ctrlKey) return;

  const panel = event.currentTarget;
  // React events bubble the REACT tree, not the DOM one, so a keypress inside
  // a portaled submenu also reaches the parent menu — whose rows are not the
  // ones being navigated.
  if (!panel.contains(event.target as Node | null)) return;

  const rows = Array.from(panel.querySelectorAll<HTMLElement>(ROW_SELECTOR));
  if (rows.length === 0) return;
  // Only once we know we'll move: otherwise an empty menu would swallow the
  // key and freeze the page's own scrolling.
  event.preventDefault();
  event.stopPropagation();

  const active = document.activeElement;
  const current = rows.findIndex((row) => row === active || row.contains(active));
  const next =
    current === -1
      ? // Nothing in the list has focus yet (the panel itself does, or the
        // search field): enter from the end the arrow points at.
        rows[step === 1 ? 0 : rows.length - 1]
      : rows[(current + step + rows.length) % rows.length];

  next.focus();
  // Focusing already scrolls a fully hidden row into view; asking explicitly
  // also covers the row that is only PARTLY cut off by the panel's edge.
  next.scrollIntoView({ block: 'nearest' });
}
